import { c as _c } from "react/compiler-runtime";
import { cn, Skeleton, Text } from '@repo/ui';
import { LoaderGradient } from '@repo/ui/icons';
import type { ActionFlowState } from '../types.action-flow';
import { EtherscanLink } from '../../../components/EtherscanLink';
import { SAFE_WALLET_TX_TIMEOUT_MINUTES, useIsSafeAccount } from '../../../hooks/safe-wallet.hooks';
import { useActionFlow } from '../ActionFlowProvider';
import { isActionFlowFinalizing } from '../utils/isActionFlowFinalizing';
function computeStepState(state: ActionFlowState, i: number) {
  if (i < state.stepIndex || state.flowState === 'success') {
    return 'complete';
  } else {
    const isActiveStep = state.flowState === 'execution' && state.stepIndex === i;
    return isActiveStep ? state.stepState : 'todo';
  }
}
export function ActionFlowSteps() {
  const $ = _c(39);
  const {
    stepLabels,
    state,
    isLoading,
    chainId,
    isCallbackInFlight
  } = useActionFlow();
  const isSafeAccount = useIsSafeAccount();
  if (isLoading) {
    let t0;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t0 = <Skeleton className="h-17 w-full" />;
      $[0] = t0;
    } else {
      t0 = $[0];
    }
    return t0;
  }
  const clampedStepIndex = Math.min(state.stepIndex, Math.max(stepLabels.length - 1, 0));
  let t0;
  if ($[1] !== clampedStepIndex || $[2] !== state) {
    t0 = computeStepState(state, clampedStepIndex);
    $[1] = clampedStepIndex;
    $[2] = state;
    $[3] = t0;
  } else {
    t0 = $[3];
  }
  const activeStepState = t0;
  let t1;
  if ($[4] !== state) {
    t1 = isActionFlowFinalizing(state);
    $[4] = state;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const isFinalizing = t1;
  const isAwaitingSafeProposal = state.flowState === "execution" && state.pendingSafeTxHash !== undefined;
  let activeStepActionItem;
  if (isCallbackInFlight) {
    activeStepActionItem = " - Waiting";
  } else {
    if (isFinalizing) {
      activeStepActionItem = " - Updating your data";
    } else {
      let t2;
      if ($[6] !== chainId || $[7] !== state.flowState || $[8] !== state.pendingTxHash) {
        t2 = state.flowState === "execution" && state.pendingTxHash && chainId && <>{" "}<EtherscanLink tx={state.pendingTxHash} chainId={chainId} className="text-secondary-foreground text-[length:inherit] underline">View</EtherscanLink></>;
        $[6] = chainId;
        $[7] = state.flowState;
        $[8] = state.pendingTxHash;
        $[9] = t2;
      } else {
        t2 = $[9];
      }
      let t3;
      if ($[10] !== t2) {
        t3 = {
          "pending-wallet": " - Proceed in your wallet",
          "pending-transaction": <>{" "}- Transaction processing{t2}</>,
          finalizing: null,
          complete: null,
          todo: null
        };
        $[10] = t2;
        $[11] = t3;
      } else {
        t3 = $[11];
      }
      activeStepActionItem = t3[activeStepState];
    }
  }
  const isProcessing = isCallbackInFlight || isFinalizing || activeStepState === "pending-wallet" || activeStepState === "pending-transaction";
  const t2 = stepLabels[clampedStepIndex];
  let t3;
  if ($[12] !== t2) {
    t3 = <Text size="md">{t2}</Text>;
    $[12] = t2;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  if ($[14] !== state || $[15] !== stepLabels) {
    let t5;
    if ($[17] !== state) {
      t5 = (_, i) => <StepIndicator key={i} state={computeStepState(state, i)} />;
      $[17] = state;
      $[18] = t5;
    } else {
      t5 = $[18];
    }
    t4 = stepLabels.map(t5);
    $[14] = state;
    $[15] = stepLabels;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  let t5;
  if ($[19] !== t4) {
    t5 = <div className="pb-xs pt-sm flex gap-1">{t4}</div>;
    $[19] = t4;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  let t6;
  if ($[21] !== isProcessing) {
    t6 = isProcessing && <LoaderGradient className="mt-0.5 size-3 shrink-0" />;
    $[21] = isProcessing;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  const t7 = clampedStepIndex + 1;
  let t8;
  if ($[23] !== activeStepActionItem || $[24] !== stepLabels.length || $[25] !== t7) {
    t8 = <span>Step {t7}/{stepLabels.length}{activeStepActionItem}</span>;
    $[23] = activeStepActionItem;
    $[24] = stepLabels.length;
    $[25] = t7;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] !== t6 || $[28] !== t8) {
    t9 = <Text size="3xs" className="text-secondary-foreground flex items-start gap-1">{t6}{t8}</Text>;
    $[27] = t6;
    $[28] = t8;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  let t10;
  if ($[30] !== activeStepState || $[31] !== isAwaitingSafeProposal || $[32] !== isSafeAccount) {
    t10 = isAwaitingSafeProposal ? <Text size="3xs" className="text-secondary-foreground mt-1">This transaction is waiting in your Safe queue. Confirm and execute it there, and don't submit it again.</Text> : isSafeAccount && (activeStepState === "pending-wallet" || activeStepState === "pending-transaction") && <Text size="3xs" className="text-secondary-foreground mt-1">Multi-sig signing may take longer — the transaction will remain active for up to{" "}{SAFE_WALLET_TX_TIMEOUT_MINUTES} minutes.</Text>;
    $[30] = activeStepState;
    $[31] = isAwaitingSafeProposal;
    $[32] = isSafeAccount;
    $[33] = t10;
  } else {
    t10 = $[33];
  }
  let t11;
  if ($[34] !== t10 || $[35] !== t3 || $[36] !== t5 || $[37] !== t9) {
    t11 = <div className="flex w-full flex-col">{t3}{t5}{t9}{t10}</div>;
    $[34] = t10;
    $[35] = t3;
    $[36] = t5;
    $[37] = t9;
    $[38] = t11;
  } else {
    t11 = $[38];
  }
  return t11;
}
function StepIndicator(t0) {
  const $ = _c(7);
  const {
    state
  } = t0;
  const t1 = state === "complete" || state === "finalizing";
  const t2 = state === "pending-wallet";
  const t3 = state === "pending-transaction";
  const t4 = state === "todo";
  let t5;
  if ($[0] !== t1 || $[1] !== t2 || $[2] !== t3 || $[3] !== t4) {
    t5 = cn("flex h-1 flex-1 items-center gap-2 rounded-full", {
      "bg-success-foreground": t1,
      "bg-info-foreground animate-pulse": t2,
      "bg-primary animate-pulse": t3,
      "bg-muted": t4
    });
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== t5) {
    t6 = <div className={t5} />;
    $[5] = t5;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  return t6;
}